import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TranslateIcon from '@mui/icons-material/Translate';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StorageIcon from '@mui/icons-material/Storage';
import { getEntityCounts } from '@/services/adminApi';

interface DashboardCard {
  title: string;
  path: string;
  icon: React.ReactNode;
  countKey: string;
  color: string;
}

const cards: DashboardCard[] = [
  {
    title: 'Tiers',
    path: '/tiers',
    icon: <LayersIcon sx={{ fontSize: 48 }} />,
    countKey: 'tiers',
    color: '#2AB6E5',
  },
  {
    title: 'Upgrade Matrix',
    path: '/upgrade-matrix',
    icon: <SwapVertIcon sx={{ fontSize: 48 }} />,
    countKey: 'upgradeRules',
    color: '#4CAF50',
  },
  {
    title: 'Access Matrix',
    path: '/access-matrix',
    icon: <LockOpenIcon sx={{ fontSize: 48 }} />,
    countKey: 'accessRules',
    color: '#FF9800',
  },
  {
    title: 'CSI Name Map',
    path: '/csi-map',
    icon: <TranslateIcon sx={{ fontSize: 48 }} />,
    countKey: 'csiMappings',
    color: '#9C27B0',
  },
  {
    title: 'Promotions',
    path: '/promotions',
    icon: <LocalOfferIcon sx={{ fontSize: 48 }} />,
    countKey: 'promotions',
    color: '#F44336',
  },
  {
    title: 'Seed Data',
    path: '/seed',
    icon: <StorageIcon sx={{ fontSize: 48 }} />,
    countKey: '',
    color: '#607D8B',
  },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadCounts() {
      try {
        const data = await getEntityCounts();
        if (!cancelled) {
          setCounts(data);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load entity counts:', err);
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to load data. Your session may have expired — try signing in again.',
          );
          setLoading(false);
        }
      }
    }

    loadCounts();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Onelife tier data administration. Select a section below to manage tier
        definitions, pricing rules, and system mappings.
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && counts.total !== undefined && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>{counts.total}</strong> total records across all entities.
        </Alert>
      )}

      <Grid container spacing={3}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.path}>
            <Card
              sx={{
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardActionArea onClick={() => navigate(card.path)}>
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 4,
                  }}
                >
                  <Box sx={{ color: card.color, mb: 2 }}>{card.icon}</Box>
                  <Typography variant="h6" gutterBottom>
                    {card.title}
                  </Typography>
                  {card.countKey && (
                    <Typography variant="h3" sx={{ color: card.color, fontWeight: 700 }}>
                      {loading ? (
                        <CircularProgress size={32} />
                      ) : (
                        counts[card.countKey] ?? 0
                      )}
                    </Typography>
                  )}
                  {card.countKey && !loading && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                      records
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: card.color, fontWeight: 600, mt: 1 }}
                  >
                    Manage &rarr;
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
